import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, ArrowUpRight } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useState } from "react";

// MILE PLUS Footer Logo
const MilePlusFooterLogo = () => (
  <div className="relative inline-block">
    <span 
      className="font-heading font-black text-2xl tracking-[0.25em] uppercase"
      style={{
        background: 'linear-gradient(135deg, #CCFF00 0%, #E8FF66 50%, #CCFF00 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      }}
    >
      MILE PLUS
    </span>
    <svg 
      className="absolute -top-1 -right-2 w-3 h-3 text-primary opacity-80" 
      viewBox="0 0 24 24" 
      fill="currentColor"
    >
      <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
    </svg>
  </div>
);

const footerLinks = {
  company: [
    { name: "About Us", href: "#" },
    { name: "Careers", href: "#" },
    { name: "Press", href: "#" },
    { name: "Blog", href: "#" },
  ],
  services: [
    { name: "Find Stations", href: "#stations" },
    { name: "Pricing", href: "#pricing" },
    { name: "Home Charging", href: "#" },
    { name: "Fleet Solutions", href: "#" },
  ],
  support: [
    { name: "Help Center", href: "#" },
    { name: "Contact Us", href: "#contact" },
    { name: "FAQ", href: "#faq" },
    { name: "Report Issue", href: "#" },
  ],
  legal: [
    { name: "Privacy Policy", href: "#" },
    { name: "Terms of Service", href: "#" },
    { name: "Cookie Policy", href: "#" },
  ],
};

const socialLinks = [
  { name: "Facebook", icon: Facebook, href: "#" },
  { name: "Twitter", icon: Twitter, href: "#" },
  { name: "Instagram", icon: Instagram, href: "#" },
  { name: "LinkedIn", icon: Linkedin, href: "#" },
  { name: "YouTube", icon: Youtube, href: "#" },
];

const Footer = () => {
  const [email, setEmail] = useState("");

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success("Thanks for subscribing! Stay tuned for updates.");
      setEmail("");
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-background-paper relative" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl text-foreground">
                Stay in the <span className="text-primary">Charge</span>
              </h3>
              <p className="text-foreground-muted mt-2">
                Subscribe to our newsletter for the latest updates and offers.
              </p>
            </div>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 w-full md:w-auto"
            >
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-background border-white/10 focus:border-primary text-foreground h-12 w-full sm:w-72"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                className="rounded-full bg-primary text-black font-bold hover:shadow-[0_0_20px_rgba(204,255,0,0.5)] transition-all h-12 px-8"
                data-testid="newsletter-submit-btn"
              >
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2">
            <motion.a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
              className="inline-block mb-6"
              whileHover={{ scale: 1.05 }}
              data-testid="footer-logo"
            >
              <MilePlusFooterLogo />
            </motion.a>
            <p className="text-foreground-muted text-sm mb-6">
              Powering Maharashtra's journey towards a sustainable future with
              reliable EV charging solutions powered by premium Cubenz chargers.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="tel:+919876543210"
                className="flex items-center gap-2 text-foreground-muted hover:text-primary transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                +91 7030808257
              </a>
              <a
                href="mailto:saurav.nagpure@MILEPLUSev.com"
                className="flex items-center gap-2 text-foreground-muted hover:text-primary transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                saurav.nagpure@MILEPLUSev.com
              </a>
              <div className="flex items-start gap-2 text-foreground-muted text-sm">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>D-216, Roongta Central Park, Dwarka, Nashik - 422001</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-sm text-foreground mb-4 uppercase tracking-wider">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-foreground-muted hover:text-primary transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-heading text-sm text-foreground mb-4 uppercase tracking-wider">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-foreground-muted hover:text-primary transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-heading text-sm text-foreground mb-4 uppercase tracking-wider">
              Support
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-foreground-muted hover:text-primary transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-heading text-sm text-foreground mb-4 uppercase tracking-wider">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-foreground-muted hover:text-primary transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-foreground-muted text-sm text-center md:text-left">
              © {new Date().getFullYear()} MILE PLUS EV Charging. All rights reserved.
              Made with{" "}
              <span className="text-primary">⚡</span> in Nashik.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-foreground-muted hover:bg-primary hover:text-black transition-all"
                  aria-label={social.name}
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-primary text-black flex items-center justify-center shadow-lg hover:shadow-[0_0_20px_rgba(204,255,0,0.5)] transition-all z-40"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="back-to-top-btn"
      >
        <ArrowUpRight className="w-6 h-6 -rotate-45" />
      </motion.button>
    </footer>
  );
};

export default Footer;
